import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { stripChapterPrefix } from '../lib/utils';

interface Chapter {
  id: number;
  chapterNum: number;
  title: string;
  wordCount: number;
  isDraft?: boolean;
  volumeId?: number | null;
}

interface Volume {
  id: number;
  storyId: number;
  title: string;
  description?: string | null;
  sortOrder: number;
  chapters: Chapter[];
}

interface VolumeManagerProps {
  storyId: number;
  allChapters: Chapter[];
  onUpdate?: () => void;
}

export default function VolumeManager({ storyId, allChapters, onUpdate }: VolumeManagerProps) {
  const [volumes, setVolumes] = useState<Volume[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewVolume, setShowNewVolume] = useState(false);
  const [newVolumeTitle, setNewVolumeTitle] = useState('');
  const [newVolumeDesc, setNewVolumeDesc] = useState('');
  const [editingVolumeId, setEditingVolumeId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [expandedVolume, setExpandedVolume] = useState<number | null>(null);
  const [assigningChapterId, setAssigningChapterId] = useState<number | null>(null);

  const loadVolumes = async () => {
    try {
      const data = await api.volumes.list(storyId);
      setVolumes(data);
    } catch (err) {
      console.error('Failed to load volumes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVolumes();
  }, [storyId]);

  const handleCreateVolume = async () => {
    if (!newVolumeTitle.trim()) return;
    setSubmitting(true);
    try {
      await api.volumes.create({
        storyId,
        title: newVolumeTitle.trim(),
        description: newVolumeDesc.trim() || undefined,
      });
      setNewVolumeTitle('');
      setNewVolumeDesc('');
      setShowNewVolume(false);
      await loadVolumes();
      onUpdate?.();
    } catch (err) {
      console.error('Create volume failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateVolume = async (id: number) => {
    setSubmitting(true);
    try {
      await api.volumes.update(id, {
        title: editTitle.trim(),
        description: editDesc.trim() || undefined,
      });
      setEditingVolumeId(null);
      await loadVolumes();
      onUpdate?.();
    } catch (err) {
      console.error('Update volume failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteVolume = async (id: number) => {
    setSubmitting(true);
    try {
      await api.volumes.delete(id);
      setDeleteConfirm(null);
      await loadVolumes();
      onUpdate?.();
    } catch (err) {
      console.error('Delete volume failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignChapter = async (chapterId: number, volumeId: number | null) => {
    setAssigningChapterId(chapterId);
    try {
      await api.volumes.assignChapter(chapterId, volumeId);
      await loadVolumes();
      onUpdate?.();
    } catch (err) {
      console.error('Assign chapter failed:', err);
    } finally {
      setAssigningChapterId(null);
    }
  };

  // Chapters not assigned to any volume
  const unassignedChapters = allChapters
    .filter(ch => !volumes.some(v => v.chapters.some(vc => vc.id === ch.id)))
    .sort((a, b) => a.chapterNum - b.chapterNum);

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <div className="w-5 h-5 border-2 border-ink-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Volume list */}
      {volumes.length === 0 && !showNewVolume && (
        <p className="text-sm text-gray-500 text-center py-3">
          No volumes yet. Create a volume to group your chapters into story arcs.
        </p>
      )}

      {volumes.map(volume => (
        <div key={volume.id} className="bg-gray-800/50 rounded-lg border border-gray-700/50">
          <div className="flex items-center justify-between px-4 py-3">
            {editingVolumeId === volume.id ? (
              <div className="flex-1 flex items-center gap-2 mr-2">
                <input
                  value={editTitle}
                  onChange={e => setEditTitle(e.target.value)}
                  className="flex-1 bg-gray-700 border border-gray-600 rounded px-3 py-1.5 text-sm focus:border-ink-500 focus:outline-none"
                  placeholder="Volume title"
                  autoFocus
                />
                <input
                  value={editDesc}
                  onChange={e => setEditDesc(e.target.value)}
                  className="flex-1 bg-gray-700 border border-gray-600 rounded px-3 py-1.5 text-sm focus:border-ink-500 focus:outline-none"
                  placeholder="Description (optional)"
                />
                <button
                  onClick={() => handleUpdateVolume(volume.id)}
                  disabled={!editTitle.trim() || submitting}
                  className="px-3 py-1.5 bg-ink-600 hover:bg-ink-500 rounded text-xs font-medium disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditingVolumeId(null)}
                  className="px-3 py-1.5 text-gray-400 hover:text-white text-xs"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                    Vol. {volume.sortOrder + 1}
                  </span>
                  <span className="font-semibold text-gray-100">{volume.title}</span>
                  <span className="text-xs text-gray-500">
                    ({volume.chapters.length} ch.)
                  </span>
                </div>
                {volume.description && (
                  <p className="text-xs text-gray-400 mt-0.5">{volume.description}</p>
                )}
              </div>
            )}

            {editingVolumeId !== volume.id && (
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => {
                    setExpandedVolume(expandedVolume === volume.id ? null : volume.id);
                  }}
                  className="px-2 py-1 text-gray-400 hover:text-white text-xs rounded hover:bg-gray-700 transition-colors"
                >
                  {expandedVolume === volume.id ? 'Hide' : 'Chapters'}
                </button>
                <button
                  onClick={() => {
                    setEditingVolumeId(volume.id);
                    setEditTitle(volume.title);
                    setEditDesc(volume.description || '');
                  }}
                  className="px-2 py-1 text-ink-400 hover:bg-ink-600/20 rounded text-xs transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => setDeleteConfirm(volume.id)}
                  className="px-2 py-1 text-red-400 hover:bg-red-900/30 rounded text-xs transition-colors"
                >
                  Delete
                </button>
              </div>
            )}
          </div>

          {/* Chapter list inside volume */}
          {expandedVolume === volume.id && (
            <div className="border-t border-gray-700/50 px-4 py-3 space-y-1.5">
              {volume.chapters.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-2">No chapters assigned to this volume yet.</p>
              ) : (
                volume.chapters.map(ch => (
                  <div key={ch.id} className="flex items-center justify-between text-sm py-1">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 w-10 shrink-0">Ch.{ch.chapterNum}</span>
                      <span className="text-gray-300">{stripChapterPrefix(ch.title)}</span>
                      <span className="text-gray-600 text-xs">{ch.wordCount?.toLocaleString()} words</span>
                    </div>
                    <button
                      onClick={() => handleAssignChapter(ch.id, null)}
                      disabled={assigningChapterId === ch.id}
                      className="text-xs text-red-400 hover:text-red-300 hover:bg-red-900/20 px-2 py-0.5 rounded transition-colors disabled:opacity-50"
                      title="Remove from this volume"
                    >
                      {assigningChapterId === ch.id ? '...' : '× Remove'}
                    </button>
                  </div>
                ))
              )}

              {/* Add unassigned chapters to this volume */}
              {unassignedChapters.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-700/30">
                  <p className="text-xs text-gray-500 mb-2">Add unassigned chapters:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {unassignedChapters.map(ch => (
                      <button
                        key={ch.id}
                        onClick={() => handleAssignChapter(ch.id, volume.id)}
                        disabled={assigningChapterId === ch.id}
                        className="px-2 py-0.5 bg-gray-700 hover:bg-gray-600 border border-gray-600 hover:border-ink-500/50 rounded text-xs text-gray-300 transition-colors disabled:opacity-50"
                      >
                        {assigningChapterId === ch.id ? '...' : `+ Ch.${ch.chapterNum}`}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Unassigned chapters summary */}
      {unassignedChapters.length > 0 && volumes.length > 0 && (
        <div className="bg-gray-800/30 rounded-lg border border-dashed border-gray-700 px-4 py-3">
          <p className="text-xs text-gray-500 mb-1.5">
            <span className="font-medium text-gray-400">{unassignedChapters.length} unassigned</span> chapter{unassignedChapters.length !== 1 ? 's' : ''}:
          </p>
          <div className="flex flex-wrap gap-1">
            {unassignedChapters.map(ch => (
              <span key={ch.id} className="px-2 py-0.5 bg-gray-700/50 rounded text-xs text-gray-400">
                Ch.{ch.chapterNum}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* New volume form */}
      {showNewVolume ? (
        <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4 space-y-3">
          <h5 className="text-sm font-semibold text-gray-300">New Volume / Story Arc</h5>
          <input
            value={newVolumeTitle}
            onChange={e => setNewVolumeTitle(e.target.value)}
            placeholder="Volume title (e.g. Volume 1: The Awakening)"
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-ink-500 focus:outline-none"
            autoFocus
            onKeyDown={e => e.key === 'Enter' && handleCreateVolume()}
          />
          <input
            value={newVolumeDesc}
            onChange={e => setNewVolumeDesc(e.target.value)}
            placeholder="Short description (optional)"
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-ink-500 focus:outline-none"
          />
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => { setShowNewVolume(false); setNewVolumeTitle(''); setNewVolumeDesc(''); }}
              className="px-3 py-1.5 text-gray-400 hover:text-white text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateVolume}
              disabled={!newVolumeTitle.trim() || submitting}
              className="px-4 py-1.5 bg-ink-600 hover:bg-ink-500 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
            >
              {submitting ? 'Creating...' : 'Create Volume'}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowNewVolume(true)}
          className="w-full py-2 border border-dashed border-gray-700 hover:border-ink-500/50 rounded-lg text-sm text-gray-500 hover:text-ink-400 transition-colors"
        >
          + Add Volume / Story Arc
        </button>
      )}

      {/* Delete confirmation */}
      {deleteConfirm !== null && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-sm w-full">
            <h3 className="text-base font-bold mb-2">Delete Volume</h3>
            <p className="text-gray-400 text-sm mb-1">
              Are you sure? Chapters in this volume will become unassigned but will not be deleted.
            </p>
            <div className="flex gap-3 justify-end mt-4">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-400 hover:text-white text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteVolume(deleteConfirm)}
                disabled={submitting}
                className="px-4 py-2 bg-red-600 hover:bg-red-500 rounded-lg text-sm font-medium disabled:opacity-50"
              >
                {submitting ? 'Deleting...' : 'Delete Volume'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
